// Energy VAD with a minimum-statistics noise floor: the floor is the
// minimum window RMS over the last few seconds, so steady background
// hum is absorbed while speech (which always has inter-word dips)
// keeps a low floor. Pure per-window decision; pre-roll/tail policy
// lives in chunker.ts.
// Design + upgrade path (Silero): docs/design-docs/audio-capture.md

export const VAD_WINDOW_MS = 30;
export const VAD_WINDOW_SAMPLES = 480; // 30ms @ 16kHz

export interface VadOptions {
  /** Speech requires rms > floor * ratio. */
  ratio: number;
  /** ...and rms above this absolute level (≈ -55 dBFS). */
  minRms: number;
  /** Sliding window length for the min-statistics floor (~4.5s). */
  floorWindows: number;
}

export const DEFAULT_VAD: VadOptions = {
  ratio: 3.0,
  minRms: 0.0018,
  floorWindows: 150,
};

export function rmsOf(window: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < window.length; i++) {
    const v = window[i] ?? 0;
    sum += v * v;
  }
  return Math.sqrt(sum / Math.max(1, window.length));
}

export class EnergyVad {
  private readonly opts: VadOptions;
  private readonly history: number[] = [];

  constructor(opts: Partial<VadOptions> = {}) {
    this.opts = { ...DEFAULT_VAD, ...opts };
  }

  /** Classify one 30 ms window. */
  isSpeech(window: Float32Array): boolean {
    const rms = rmsOf(window);
    // Threshold from PRIOR history only — pushing first would let a
    // loud cold-start window poison its own floor.
    const threshold = Math.max(
      this.noiseFloor() * this.opts.ratio,
      this.opts.minRms,
    );
    this.history.push(rms);
    if (this.history.length > this.opts.floorWindows) {
      this.history.shift();
    }
    return rms > threshold;
  }

  /** Min RMS over the recent history. Exposed for meters/debugging. */
  noiseFloor(): number {
    let min = Infinity;
    for (const v of this.history) {
      if (v < min) min = v;
    }
    return Number.isFinite(min) ? min : 0;
  }
}
